import { PERK_REDEMPTION_STATUS } from "~constants/status"
import { ModalTitle } from "~styled/modal"
import Modal from "~app/Modal"
import { useEffect, useState } from "react"
import RedemptionService from "~services/RedemptionService"
import { toast } from "react-toastify"
import { ERROR_MESSAGES } from "~constants/errorMessages"
import { FormProvider, useForm } from "react-hook-form"
import ControlSelect from "~app/ControlSelect"
import { WizardColumn, WizardRow } from "~styled/wizard"
import { createDropdownCollection, SelectOption } from "~utils/dropdown"
import ConfirmationModal from "~components/ConfirmationModal"

export enum CURRENCIES {
  USD = "USD",
  EUR = "EUR",
  GBP = "GBP",
  AUD = "AUD",
}

interface Props {
  isOpen: boolean
  setIsOpen: (v: boolean) => void
  selectedRedemptionId: string
  refreshList: () => void
  bookingNumber?: string
}

export const handleRedemptionStatusChange = (
  redemptionId: string,
  status: PERK_REDEMPTION_STATUS,
  invalidationReason: string | null = null,
  invalidationDetails: string | null = null,
  currency: string | null = null,
  amount: string | null = null,
  beforeRequest?: Function,
  onSuccess?: Function,
  onFinally?: Function,
): void => {
  if (beforeRequest) {
    beforeRequest()
  }
  RedemptionService.patchRedemption({
    redemptionId,
    status,
    invalidationReason,
    invalidationDetails,
    currency,
    amount,
  })
    .then(() => {
      if (onSuccess) {
        onSuccess()
      }
      toast.success(`Redemption status successfully updated.`)
    })
    .catch(() => {
      toast.error(`There was an error updating the redemption status.`)
    })
    .finally(() => {
      if (onFinally) {
        onFinally()
      }
    })
}

const CCLApprovalModal = ({
  isOpen,
  setIsOpen,
  selectedRedemptionId,
  refreshList,
  bookingNumber,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const methods = useForm()
  const {
    formState: { errors },
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
  } = methods

  const currenciesList = createDropdownCollection(
    [
      { label: CURRENCIES.USD, value: CURRENCIES.USD },
      { label: CURRENCIES.EUR, value: CURRENCIES.EUR },
      { label: CURRENCIES.GBP, value: CURRENCIES.GBP },
      { label: CURRENCIES.AUD, value: CURRENCIES.AUD },
    ],
    "label",
    "value",
  )

  const amountOptions = {
    USD: [250, 200, 150, 100, 50],
    EUR: [200, 75, 40],
    GBP: [150, 60, 30],
    AUD: [250, 100, 50],
  }

  const [cclOnboardCreditValueOpts, setCclOnboardCreditValueOpts] = useState<
    SelectOption[]
  >([])

  const updateAmountOptions = () => {
    const selectedCurrency = getValues().cclOnboardCreditCurrency?.value
    const options =
      (amountOptions[selectedCurrency] || []).map(value => ({
        label: value.toString(),
        value: value.toString(),
      })) || []
    setCclOnboardCreditValueOpts(options)
  }

  useEffect(() => {
    updateAmountOptions()
  }, [watch("cclOnboardCreditCurrency")])

  useEffect(() => {
    setValue("cclOnboardCreditCurrency", { label: "", value: "" })
    setValue("cclOnboardCreditValue", { label: "", value: "" })
  }, [])

  const onSubmit = () => {
    setShowConfirmation(true)
  }

  const handleConfirm = () => {
    handleRedemptionStatusChange(
      selectedRedemptionId,
      PERK_REDEMPTION_STATUS.REDEEMED,
      null,
      null,
      getValues().cclOnboardCreditCurrency.value,
      getValues().cclOnboardCreditValue.value,
      () => setIsLoading(true),
      () => {
        refreshList()
        setIsOpen(false)
      },
      () => setIsLoading(false),
    )
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        buttonLabel={"Next"}
        isButtonDisabled={isLoading}
        isButtonLoading={isLoading}
        cancelButton={true}
        onRequestClose={() => setIsOpen(false)}
        onRequestConfirm={handleSubmit(onSubmit)}
      >
        <FormProvider {...methods}>
          <ModalTitle>OBC Request Approved</ModalTitle>
          {bookingNumber && (
            <p>
              <strong>Booking Number:</strong> {bookingNumber}
            </p>
          )}
          <WizardRow>
            <WizardColumn>
              <ControlSelect
                name={"cclOnboardCreditCurrency"}
                label={"Currency"}
                rules={{ required: true }}
                control={control}
                options={currenciesList}
                disabled={false}
                searchable={false}
                errorMessage={
                  errors.cclOnboardCreditCurrency &&
                  ERROR_MESSAGES.FIELD_REQUIRED
                }
              />
            </WizardColumn>
            <WizardColumn>
              <ControlSelect
                name="cclOnboardCreditValue"
                label="Amount"
                control={control}
                options={cclOnboardCreditValueOpts}
                rules={{ required: true }}
                errorMessage={
                  errors.cclOnboardCreditValue && ERROR_MESSAGES.FIELD_REQUIRED
                }
              />
            </WizardColumn>
          </WizardRow>
          <WizardRow>
            <p>
              When you hit confirm, the guest will be sent an email confirming
              that they will receive an OBC on their upcoming reservation in the
              currency and amount you selected.
            </p>
          </WizardRow>
        </FormProvider>
      </Modal>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirm}
        message="This action cannot be reverted, are you sure you want to Approve this OBC?"
        bookingNumber={bookingNumber}
      />
    </>
  )
}

export default CCLApprovalModal
